import React, { ReactElement, useState } from 'react'
import { gql, useQuery } from '@apollo/client'
import { Button } from '@lightspeed/design-system-react'
import { ProductBadge } from '@vend/react-utilities'
import keyBy from 'lodash/keyBy'
import {
  WorkflowsEvent,
  WorkflowsSuggestProductsAction,
  WorkflowsDialog,
  WorkflowsDialogProps,
} from '@vend/business-components'
import classNames from 'classnames'

export interface Product {
  __typename: 'Product'
  id: string
  name: string
  sku: string
  imageThumbnailURL: string
  hasVariants: boolean
  variantParentID: string | null
  variantName: string | null
  isComposite: boolean
  variantCount: number | null
}

export const SuggestProductsProductQuery = gql`
  query SuggestProductsProductQuery(
    $productIds: [String!]
    $productSkus: [String!]
  ) {
    products(filter: { include: { ids: $productIds, skus: $productSkus } }) {
      products {
        id
        name
        sku
        imageThumbnailURL
        hasVariants
        variantParentID
        variantName
        isComposite
        variantCount
      }
    }
  }
`

export interface Manager {
  addLineItem(
    product: {
      id?: string
      sku?: string
    },
    options: {
      quantity: number | string
      price?: string
      note?: string
    },
    event?: WorkflowsEvent
  ): Promise<boolean>
}

export interface SuggestProductsActionHandlerProps
  extends Pick<WorkflowsDialogProps, 'onOutcome'> {
  event: WorkflowsEvent
  action: WorkflowsSuggestProductsAction
  manager: Manager
}

export const SuggestProductsActionHandler = ({
  event,
  action,
  manager,
  onOutcome,
}: SuggestProductsActionHandlerProps): ReactElement => {
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([])
  const toggleProduct = (product: Product) => {
    const newSelectedProducts = [...selectedProducts]
    const index = newSelectedProducts.indexOf(product)

    if (index === -1) {
      newSelectedProducts.push(product)
    } else {
      newSelectedProducts.splice(index, 1)
    }

    setSelectedProducts(newSelectedProducts)
  }

  const productIds: string[] = []
  const productSkus: string[] = []
  action.suggested_products.forEach(sp => {
    if (sp.product_id) {
      productIds.push(sp.product_id)
    } else if (sp.product_sku) {
      productSkus.push(sp.product_sku)
    }
  })

  const { data } = useQuery<
    { products: { products: Product[] } },
    { productIds: string[]; productSkus: string[] }
  >(SuggestProductsProductQuery, {
    variables: { productIds, productSkus },
    skip: productIds.length + productSkus.length === 0,
  })

  const productsById = keyBy(data?.products.products, product => product.id)
  const productsBySku = keyBy(data?.products.products, product => product.sku)

  const addProductsToSale = async () => {
    const addLineItemPromisses = selectedProducts.map(product =>
      manager.addLineItem(product, { quantity: 1 }, event)
    )

    const modified = await Promise.all(addLineItemPromisses).then(results =>
      results.reduce((modified, added) => modified || added, false)
    )

    onOutcome({ proceed: true, modified })
  }

  const moreThanOneSuggestion = action.suggested_products.length > 1

  return (
    <WorkflowsDialog
      title={action.title}
      application={action.application}
      onOutcome={onOutcome}
      anchoredTo={event.anchoredTo}
      actions={
        <>
          <Button
            variant="supplementary"
            onClick={() => {
              onOutcome({ proceed: true, modified: false })
            }}
          >
            No Thanks
          </Button>
          <Button
            variant="go"
            disabled={selectedProducts.length === 0}
            onClick={addProductsToSale}
          >
            Add Products to Basket
          </Button>
        </>
      }
    >
      <div
        className="vd-p"
        style={{ whiteSpace: 'pre-wrap' }}
        dangerouslySetInnerHTML={{ __html: action.message }}
      />
      <div
        className={classNames('vd-g-row', {
          'vd-g-s-up-1': !moreThanOneSuggestion,
          'vd-g-s-up-2': moreThanOneSuggestion,
        })}
      >
        {action.suggested_products.map(sp => {
          const product =
            productsById[sp.product_id || ''] ||
            productsBySku[sp.product_sku || '']
          if (!product) return null

          return (
            <div
              className="vd-g-col vd-pa1"
              key={product.id}
              style={{ minWidth: moreThanOneSuggestion ? '240px' : '100%' }}
            >
              <ProductBadge
                variant
                product={product}
                interactive="selection"
                current={selectedProducts.includes(product)}
                onClick={() => toggleProduct(product)}
              />
            </div>
          )
        })}
      </div>
    </WorkflowsDialog>
  )
}
